import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  X, ChevronRight, ChevronLeft, Users, Building2, UserPlus,
  FileText, Sparkles, Check, Loader2, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, label: 'Client Details', icon: Users, desc: 'Basic client information' },
  { id: 2, label: 'Individuals', icon: UserPlus, desc: 'Family members & key people' },
  { id: 3, label: 'Entities', icon: Building2, desc: 'Trusts, companies, SMSFs' },
  { id: 4, label: 'Advisers', icon: Users, desc: 'Professional network' },
  { id: 5, label: 'Review', icon: Check, desc: 'Review & create' }
];

const SEGMENTS = ['UHNW', 'HNW', 'Affluent', 'Institutional', 'Not-for-profit'];
const INDIVIDUAL_ROLES = ['Principal', 'Spouse', 'Child', 'Trustee', 'Beneficiary', 'Director', 'Other'];
const ENTITY_TYPES = ['Family Trust', 'Unit Trust', 'Company', 'SMSF', 'Foundation', 'Partnership'];
const ADVISER_ROLES = ['Accountant', 'Lawyer', 'Financial Adviser', 'Insurance Broker', 'Mortgage Broker', 'Other'];

function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-1" data-testid="intake-step-indicator">
      {steps.map((step, i) => {
        const Icon = step.icon;
        const isActive = step.id === current;
        const isDone = step.id < current;
        return (
          <div key={step.id} className="flex items-center">
            <div className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
              isActive && "bg-[#4A3B32] text-white",
              isDone && "bg-green-100 text-green-700",
              !isActive && !isDone && "text-[#8C8078]"
            )}>
              {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              <span className="text-xs font-medium hidden sm:inline">{step.label}</span>
            </div>
            {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-[#D4CCC6] mx-1" />}
          </div>
        );
      })}
    </div>
  );
}

export default function ClientIntakeWizard({ isOpen, onClose, onComplete }) {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);

  // Step data
  const [clientData, setClientData] = useState({ name: '', segment: 'HNW', netWorth: '', relationshipLead: '', referralSource: '', notes: '' });
  const [individuals, setIndividuals] = useState([]);
  const [entities, setEntities] = useState([]);
  const [advisers, setAdvisers] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setDraftId(null);
      setClientData({ name: '', segment: 'HNW', netWorth: '', relationshipLead: '', referralSource: '', notes: '' });
      setIndividuals([]);
      setEntities([]);
      setAdvisers([]);
      setShowAiPanel(false);
      setAiText('');
    }
  }, [isOpen]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (!draftId) {
        const res = await api.createIntakeDraft(token, { name: clientData.name || 'New Client', steps: {} });
        setDraftId(res.data.draft.id);
        await api.updateIntakeDraft(token, res.data.draft.id, { step, data: getStepData() });
      } else {
        await api.updateIntakeDraft(token, draftId, { step, data: getStepData() });
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const getStepData = () => {
    switch (step) {
      case 1: return clientData;
      case 2: return { individuals };
      case 3: return { entities };
      case 4: return { advisers };
      default: return {};
    }
  };

  const handleNext = async () => {
    await saveDraft();
    setStep(prev => Math.min(prev + 1, 5));
  };

  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));

  const handleAiPrefill = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const res = await api.aiPreFillIntake(token, { text: aiText });
      const d = res.data.extracted || {};
      if (d.client) setClientData(prev => ({ ...prev, ...Object.fromEntries(Object.entries(d.client).filter(([, v]) => v)) }));
      if (d.individuals?.length) setIndividuals(d.individuals.map((ind, i) => ({ ...ind, _key: `ai-${i}` })));
      if (d.entities?.length) setEntities(d.entities.map((ent, i) => ({ ...ent, _key: `ai-${i}` })));
      if (d.advisers?.length) setAdvisers(d.advisers.map((adv, i) => ({ ...adv, _key: `ai-${i}` })));
      toast.success('AI extracted data successfully');
      setShowAiPanel(false);
    } catch (e) {
      toast.error('AI extraction failed');
    }
    setAiLoading(false);
  };

  const handleFinalize = async () => {
    setSaving(true);
    try {
      // Save all steps first
      let id = draftId;
      if (!id) {
        const res = await api.createIntakeDraft(token, { name: clientData.name || 'New Client' });
        id = res.data.draft.id;
        setDraftId(id);
      }
      await api.updateIntakeDraft(token, id, { step: 1, data: clientData });
      await api.updateIntakeDraft(token, id, { step: 2, data: { individuals } });
      await api.updateIntakeDraft(token, id, { step: 3, data: { entities } });
      await api.updateIntakeDraft(token, id, { step: 4, data: { advisers } });

      const res = await api.finalizeIntake(token, id);
      toast.success(`Client "${clientData.name}" created successfully!`);
      onComplete?.(res.data.clientId);
      onClose();
    } catch (e) {
      toast.error('Failed to create client');
    }
    setSaving(false);
  };

  const addItem = (setter) => setter(prev => [...prev, { _key: `new-${Date.now()}` }]);
  const removeItem = (setter, index) => setter(prev => prev.filter((_, i) => i !== index));
  const updateItem = (setter, index, field, value) => setter(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-4 sm:inset-8 bg-white rounded-2xl shadow-2xl z-[61] flex flex-col overflow-hidden" data-testid="client-intake-wizard">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E0D9] bg-[#FAFAF8]">
          <div>
            <h2 className="font-heading text-lg text-[#2C2420]">Client Intake</h2>
            <p className="text-xs text-[#8C8078]">Step {step} of 5 — {STEPS[step - 1].desc}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setShowAiPanel(!showAiPanel)} className="text-xs" data-testid="ai-prefill-toggle">
              <Sparkles className="w-4 h-4 mr-1" />AI Pre-Fill
            </Button>
            <button onClick={onClose} className="text-[#8C8078] hover:text-[#2C2420]"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Steps */}
        <div className="px-6 py-3 border-b border-[#E6E0D9] overflow-x-auto">
          <StepIndicator steps={STEPS} current={step} />
        </div>

        {/* AI Panel */}
        {showAiPanel && (
          <div className="px-6 py-4 border-b border-[#E6E0D9] bg-purple-50" data-testid="ai-prefill-panel">
            <p className="text-sm font-medium text-purple-900 mb-2">Paste client notes, referral emails, or document text below:</p>
            <textarea value={aiText} onChange={(e) => setAiText(e.target.value)}
              placeholder="e.g. 'John and Mary Baker, HNW couple. John is a retired CEO. They have a family trust (Baker Family Trust) managed by Smith & Associates...'"
              className="w-full h-24 text-sm border border-purple-200 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
              data-testid="ai-prefill-input" />
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={handleAiPrefill} disabled={aiLoading || !aiText.trim()} className="bg-purple-700 hover:bg-purple-800" data-testid="ai-prefill-submit">
                {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {aiLoading ? 'Extracting...' : 'Extract Data'}
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Step 1: Client Details */}
          {step === 1 && (
            <div className="space-y-4 max-w-2xl" data-testid="intake-step-1">
              <div><label className="text-xs font-medium text-[#5D524D] mb-1 block">Client / Family Name *</label>
                <Input value={clientData.name} onChange={(e) => setClientData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Baker Family Group" data-testid="intake-client-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-medium text-[#5D524D] mb-1 block">Segment</label>
                  <select value={clientData.segment} onChange={(e) => setClientData(p => ({ ...p, segment: e.target.value }))} className="w-full text-sm border border-[#E6E0D9] rounded-lg px-3 py-2" data-testid="intake-segment">
                    {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select></div>
                <div><label className="text-xs font-medium text-[#5D524D] mb-1 block">Net Worth</label>
                  <Input value={clientData.netWorth} onChange={(e) => setClientData(p => ({ ...p, netWorth: e.target.value }))} placeholder="e.g. $5M - $10M" data-testid="intake-net-worth" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-medium text-[#5D524D] mb-1 block">Relationship Lead</label>
                  <Input value={clientData.relationshipLead} onChange={(e) => setClientData(p => ({ ...p, relationshipLead: e.target.value }))} placeholder="Primary adviser" data-testid="intake-rel-lead" /></div>
                <div><label className="text-xs font-medium text-[#5D524D] mb-1 block">Referral Source</label>
                  <Input value={clientData.referralSource} onChange={(e) => setClientData(p => ({ ...p, referralSource: e.target.value }))} placeholder="How they found you" data-testid="intake-referral" /></div>
              </div>
              <div><label className="text-xs font-medium text-[#5D524D] mb-1 block">Notes</label>
                <textarea value={clientData.notes} onChange={(e) => setClientData(p => ({ ...p, notes: e.target.value }))} className="w-full h-20 text-sm border border-[#E6E0D9] rounded-lg p-3 resize-none" placeholder="Background, context..." data-testid="intake-notes" /></div>
            </div>
          )}

          {/* Step 2: Individuals */}
          {step === 2 && (
            <div data-testid="intake-step-2">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#5D524D]">{individuals.length} individual{individuals.length !== 1 ? 's' : ''}</p>
                <Button size="sm" variant="outline" onClick={() => addItem(setIndividuals)} data-testid="add-individual-btn"><Plus className="w-4 h-4 mr-1" />Add Individual</Button>
              </div>
              <div className="space-y-3">
                {individuals.map((ind, i) => (
                  <div key={ind._key || i} className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-[#5D524D]">Individual {i + 1}</span>
                      <button onClick={() => removeItem(setIndividuals, i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input value={ind.firstName || ''} onChange={(e) => updateItem(setIndividuals, i, 'firstName', e.target.value)} placeholder="First name" className="text-sm" />
                      <Input value={ind.lastName || ''} onChange={(e) => updateItem(setIndividuals, i, 'lastName', e.target.value)} placeholder="Last name" className="text-sm" />
                      <select value={ind.role || ''} onChange={(e) => updateItem(setIndividuals, i, 'role', e.target.value)} className="text-sm border border-[#E6E0D9] rounded-lg px-3 py-2">
                        <option value="">Role...</option>
                        {INDIVIDUAL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <Input value={ind.email || ''} onChange={(e) => updateItem(setIndividuals, i, 'email', e.target.value)} placeholder="Email" className="text-sm" />
                      <Input value={ind.phone || ''} onChange={(e) => updateItem(setIndividuals, i, 'phone', e.target.value)} placeholder="Phone" className="text-sm" />
                    </div>
                  </div>
                ))}
                {individuals.length === 0 && <div className="text-center py-12 text-sm text-[#8C8078]">No individuals added yet. Click "Add Individual" to start.</div>}
              </div>
            </div>
          )}

          {/* Step 3: Entities */}
          {step === 3 && (
            <div data-testid="intake-step-3">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#5D524D]">{entities.length} entit{entities.length !== 1 ? 'ies' : 'y'}</p>
                <Button size="sm" variant="outline" onClick={() => addItem(setEntities)} data-testid="add-entity-btn"><Plus className="w-4 h-4 mr-1" />Add Entity</Button>
              </div>
              <div className="space-y-3">
                {entities.map((ent, i) => (
                  <div key={ent._key || i} className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-[#5D524D]">Entity {i + 1}</span>
                      <button onClick={() => removeItem(setEntities, i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input value={ent.name || ''} onChange={(e) => updateItem(setEntities, i, 'name', e.target.value)} placeholder="Entity name" className="text-sm" />
                      <select value={ent.type || ''} onChange={(e) => updateItem(setEntities, i, 'type', e.target.value)} className="text-sm border border-[#E6E0D9] rounded-lg px-3 py-2">
                        <option value="">Type...</option>
                        {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <Input value={ent.role || ''} onChange={(e) => updateItem(setEntities, i, 'role', e.target.value)} placeholder="Purpose / role" className="text-sm" />
                    </div>
                  </div>
                ))}
                {entities.length === 0 && <div className="text-center py-12 text-sm text-[#8C8078]">No entities added yet.</div>}
              </div>
            </div>
          )}

          {/* Step 4: Advisers */}
          {step === 4 && (
            <div data-testid="intake-step-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#5D524D]">{advisers.length} adviser{advisers.length !== 1 ? 's' : ''}</p>
                <Button size="sm" variant="outline" onClick={() => addItem(setAdvisers)} data-testid="add-adviser-btn"><Plus className="w-4 h-4 mr-1" />Add Adviser</Button>
              </div>
              <div className="space-y-3">
                {advisers.map((adv, i) => (
                  <div key={adv._key || i} className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-[#5D524D]">Adviser {i + 1}</span>
                      <button onClick={() => removeItem(setAdvisers, i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input value={adv.name || ''} onChange={(e) => updateItem(setAdvisers, i, 'name', e.target.value)} placeholder="Name" className="text-sm" />
                      <select value={adv.role || ''} onChange={(e) => updateItem(setAdvisers, i, 'role', e.target.value)} className="text-sm border border-[#E6E0D9] rounded-lg px-3 py-2">
                        <option value="">Role...</option>
                        {ADVISER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <Input value={adv.firm || ''} onChange={(e) => updateItem(setAdvisers, i, 'firm', e.target.value)} placeholder="Firm" className="text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <Input value={adv.email || ''} onChange={(e) => updateItem(setAdvisers, i, 'email', e.target.value)} placeholder="Email" className="text-sm" />
                      <Input value={adv.phone || ''} onChange={(e) => updateItem(setAdvisers, i, 'phone', e.target.value)} placeholder="Phone" className="text-sm" />
                    </div>
                  </div>
                ))}
                {advisers.length === 0 && <div className="text-center py-12 text-sm text-[#8C8078]">No advisers added yet.</div>}
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <div className="max-w-2xl space-y-6" data-testid="intake-step-5">
              <div className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                <h3 className="font-heading text-sm text-[#2C2420] mb-3">Client Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-[#8C8078]">Name:</span> <span className="font-medium">{clientData.name || '—'}</span></div>
                  <div><span className="text-[#8C8078]">Segment:</span> <span className="font-medium">{clientData.segment}</span></div>
                  <div><span className="text-[#8C8078]">Net Worth:</span> <span className="font-medium">{clientData.netWorth || '—'}</span></div>
                  <div><span className="text-[#8C8078]">Lead:</span> <span className="font-medium">{clientData.relationshipLead || '—'}</span></div>
                </div>
              </div>
              <div className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                <h3 className="font-heading text-sm text-[#2C2420] mb-2">{individuals.length} Individual{individuals.length !== 1 ? 's' : ''}</h3>
                {individuals.map((ind, i) => <p key={i} className="text-sm text-[#5D524D]">{ind.firstName} {ind.lastName} — {ind.role || 'No role'}</p>)}
                {individuals.length === 0 && <p className="text-sm text-[#8C8078]">None</p>}
              </div>
              <div className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                <h3 className="font-heading text-sm text-[#2C2420] mb-2">{entities.length} Entit{entities.length !== 1 ? 'ies' : 'y'}</h3>
                {entities.map((ent, i) => <p key={i} className="text-sm text-[#5D524D]">{ent.name} — {ent.type || 'Unknown type'}</p>)}
                {entities.length === 0 && <p className="text-sm text-[#8C8078]">None</p>}
              </div>
              <div className="bg-[#FAFAF8] rounded-lg border border-[#E6E0D9] p-4">
                <h3 className="font-heading text-sm text-[#2C2420] mb-2">{advisers.length} Adviser{advisers.length !== 1 ? 's' : ''}</h3>
                {advisers.map((adv, i) => <p key={i} className="text-sm text-[#5D524D]">{adv.name} — {adv.role || ''} at {adv.firm || '—'}</p>)}
                {advisers.length === 0 && <p className="text-sm text-[#8C8078]">None</p>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#E6E0D9] bg-[#FAFAF8]">
          <Button variant="ghost" onClick={handleBack} disabled={step === 1} data-testid="intake-back-btn"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            {step < 5 ? (
              <Button onClick={handleNext} className="bg-[#4A3B32] hover:bg-[#3A2E26]" disabled={step === 1 && !clientData.name.trim()} data-testid="intake-next-btn">
                Next<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleFinalize} className="bg-green-700 hover:bg-green-800" disabled={saving} data-testid="intake-finalize-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Create Client
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
